import type { ColDef, IRowNode, ValueGetterParams } from "ag-grid-community";

import { getPropertyByPath } from "../../../propertyPath";
import { isGroupRow } from "./serverRowGroupData";

/** Key of the group that collects rows without a value; sorted after every other group. */
export const EMPTY_ROW_GROUP_KEY = "__empty__";

/** Keeps the incoming order of groups, with the empty group last. */
export function compareRowGroupKeys(a: string | null | undefined, b: string | null | undefined): number {
  return Number(a === EMPTY_ROW_GROUP_KEY) - Number(b === EMPTY_ROW_GROUP_KEY);
}

/** The column's value in a group's first row: every row of the group shares it. */
function firstRowValue(col: ColDef, group: IRowNode): { row: IRowNode; value: unknown } | undefined {
  const row = group.allLeafChildren?.[0];
  if (!row?.data || typeof col.valueGetter === "string") return undefined;
  const data: unknown = row.data;
  const value = col.valueGetter
    ? col.valueGetter({
        data,
        node: row,
        colDef: col,
        getValue: (field: string) => getPropertyByPath(data, field),
      } as ValueGetterParams)
    : col.field && getPropertyByPath(data, col.field);
  return { row, value };
}

/**
 * Group rows sort as the column sorts its cells, by each group's first row (a reference's key is an
 * id); the empty group stays last either way, and without a comparator the key decides.
 */
export function groupKeyComparator(col: ColDef): ColDef["comparator"] {
  const existing = col.comparator;
  return (valueA, valueB, nodeA, nodeB, isDescending) => {
    if (!isGroupRow(nodeA) || !isGroupRow(nodeB)) return existing?.(valueA, valueB, nodeA, nodeB, isDescending) ?? 0;
    const empty = compareRowGroupKeys(nodeA.key, nodeB.key);
    if (empty !== 0) return isDescending ? -empty : empty;
    const firstA = existing && firstRowValue(col, nodeA);
    const firstB = existing && firstRowValue(col, nodeB);
    const byValue = firstA && firstB ? existing(firstA.value, firstB.value, firstA.row, firstB.row, isDescending) : 0;
    return byValue || String(nodeA.key).localeCompare(String(nodeB.key));
  };
}

function compareCellValues(a: unknown, b: unknown): number {
  if (a == null || b == null) return Number(a != null) - Number(b != null);
  if (typeof a === "number" && typeof b === "number") return a - b;
  return typeof a === "string" && typeof b === "string" ? a.localeCompare(b, undefined, { numeric: true }) : 0;
}

/**
 * The Group column's order: its groups as the column they are grouped by orders them, its rows as
 * the column it stands in for orders its cells. AG Grid would otherwise compare a group's raw cell
 * value, which for most columns is an object.
 */
export function rowGroupColumnComparator(leafColumn: ColDef): NonNullable<ColDef["comparator"]> {
  return (valueA, valueB, nodeA, nodeB, isDescending) => {
    const groupColumn = nodeA.rowGroupColumn;
    if (groupColumn && isGroupRow(nodeA) && isGroupRow(nodeB)) {
      return groupColumn.getColDef().comparator?.(valueA, valueB, nodeA, nodeB, isDescending) ?? 0;
    }
    return leafColumn.comparator?.(valueA, valueB, nodeA, nodeB, isDescending) ?? compareCellValues(valueA, valueB);
  };
}
